// Sidebar de bee camp — identidad "panal" de la familia (patrón carpenter/cells):
// nodos hexagonales sobre una espina punteada amarilla, 208px expandido /
// 64px colapsado. SIEMPRE oscuro en ambos temas (el chrome no cambia).
// Estilos en index.css (.bee-navbar y descendientes).
import { useState, type ReactNode } from 'react';
import { Link, NavLink } from 'react-router';
import { LogoCamp } from './LogoCamp';
import { useAuth } from '../../auth/AuthContext';
import type { PermKey } from '../../lib/types';

const COLLAPSE_KEY = 'bee_nav';

// En móvil el sidebar tapa el contenido: se considera "estrecho" ≤760px para
// arrancar colapsado y auto-colapsar al navegar, sin tocar el flujo de desktop.
const isNarrow = () =>
  typeof window !== 'undefined' && window.matchMedia('(max-width: 760px)').matches;

// Hexágono pointy-top del nodo (misma geometría que bee-cells).
function Hex() {
  return (
    <svg className="hex" viewBox="0 0 28 32" aria-hidden="true">
      <polygon points="14,1 27,8.5 27,23.5 14,31 1,23.5 1,8.5" />
    </svg>
  );
}

// Íconos inline (trazo 1.7, heredan currentColor vía CSS) — sin librerías.
const ICONS: Record<string, ReactNode> = {
  dashboard: (
    <svg viewBox="0 0 24 24">
      <rect x="3" y="3" width="7" height="9" rx="1" />
      <rect x="14" y="3" width="7" height="5" rx="1" />
      <rect x="14" y="12" width="7" height="9" rx="1" />
      <rect x="3" y="16" width="7" height="5" rx="1" />
    </svg>
  ),
  tareas: (
    <svg viewBox="0 0 24 24">
      <path d="M9 6h12M9 12h12M9 18h12" />
      <path d="M3.5 6l1.2 1.2L7 4.9" />
      <path d="M3.5 12l1.2 1.2L7 10.9" />
      <path d="M3.5 18l1.2 1.2L7 16.9" />
    </svg>
  ),
  // Panorama: eje de tiempo con barras escalonadas (la lectura gantt de la página).
  panorama: (
    <svg viewBox="0 0 24 24">
      <path d="M3.2 3.5v17h17.6" />
      <path d="M6.5 7h8" />
      <path d="M9.5 11.5h9" />
      <path d="M6.5 16h5.5" />
    </svg>
  ),
  chat: (
    <svg viewBox="0 0 24 24">
      <path d="M21 12a8 8 0 0 1-8 8H4l2-3.2A8 8 0 1 1 21 12z" />
    </svg>
  ),
  celulas: (
    <svg viewBox="0 0 24 24">
      <path d="M8.5 3.5h-4l-2 3.5 2 3.5h4l2-3.5z" />
      <path d="M19.5 3.5h-4l-2 3.5 2 3.5h4l2-3.5z" />
      <path d="M14 13.5h-4l-2 3.5 2 3.5h4l2-3.5z" />
    </svg>
  ),
  directorio: (
    <svg viewBox="0 0 24 24">
      <circle cx="9" cy="8" r="3.2" />
      <path d="M3.5 19.5c0-3 2.5-5 5.5-5s5.5 2 5.5 5" />
      <path d="M16.5 5.5a3.2 3.2 0 0 1 0 5.4" />
      <path d="M17.5 14.8c1.9.7 3 2.3 3 4.7" />
    </svg>
  ),
  papelera: (
    <svg viewBox="0 0 24 24">
      <path d="M4 7h16" />
      <path d="M9 7V4.5h6V7" />
      <path d="M6 7l1 13.5h10L18 7" />
      <path d="M10 11v6M14 11v6" />
    </svg>
  ),
  admin: (
    <svg viewBox="0 0 24 24">
      <circle cx="12" cy="12" r="3" />
      <path d="M12 2.8v3M12 18.2v3M2.8 12h3M18.2 12h3M5.5 5.5l2.1 2.1M16.4 16.4l2.1 2.1M18.5 5.5l-2.1 2.1M7.6 16.4l-2.1 2.1" />
    </svg>
  ),
};

const ITEMS: { key: string; to: string; label: string; end?: boolean }[] = [
  { key: 'dashboard', to: '/', label: 'Dashboard', end: true },
  { key: 'tareas', to: '/mis-tareas', label: 'Mis tareas' },
  { key: 'panorama', to: '/panorama', label: 'Panorama' },
  { key: 'chat', to: '/chat', label: 'Chat' },
  { key: 'celulas', to: '/celulas', label: 'Células' },
  { key: 'directorio', to: '/directorio', label: 'Directorio' },
  { key: 'papelera', to: '/papelera', label: 'Papelera' },
  { key: 'admin', to: '/admin', label: 'Admin' },
];

// Panorama vive detrás de permiso (catálogo del server, módulo PANORAMA).
const PERM_PANORAMA: PermKey = 'PANORAMA.PANORAMA.VER';

export function BeeNavbar({ isAdmin = false }: { isAdmin?: boolean }) {
  const { can } = useAuth();
  // Lazy init: se lee localStorage UNA vez, antes del primer paint, para que
  // el sidebar no "brinque" de expandido a colapsado al montar. En móvil se
  // fuerza colapsado aunque la preferencia guardada diga expandido (tapaba todo).
  const [collapsed, setCollapsed] = useState<boolean>(
    () => isNarrow() || localStorage.getItem(COLLAPSE_KEY) === '1',
  );

  const toggle = () => {
    setCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem(COLLAPSE_KEY, next ? '1' : '0');
      return next;
    });
  };

  // Al navegar en móvil, colapsa para devolver el contenido (no persiste la
  // preferencia: en desktop el usuario mantiene lo que eligió).
  const handleNavigate = () => {
    if (isNarrow()) setCollapsed(true);
  };

  // Admin por rol; panorama por permiso: si no lo tienes, la entrada ni se pinta
  // (la ruta además se protege sola, esto solo evita ofrecer lo que no puedes ver).
  const items = ITEMS.filter(
    (it) =>
      (it.key !== 'admin' || isAdmin) && (it.key !== 'panorama' || can(PERM_PANORAMA)),
  );

  return (
    <aside className={collapsed ? 'bee-navbar collapsed' : 'bee-navbar'}>
      {/* La marca lleva a Inicio, igual que el logo de la barra en modo panal:
          "que el logo sirva como home" vale en los dos modos. */}
      <Link className="nav-brand" to="/" aria-label="Ir al inicio">
        <LogoCamp size={34} />
        <span className="word">camp</span>
      </Link>

      <nav className="nav-rail" aria-label="Navegación principal">
        {items.map((it) => (
          <NavLink
            key={it.key}
            to={it.to}
            end={it.end}
            className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')}
            // Colapsado, la etiqueta se oculta con CSS y el enlace se queda sin
            // nombre visible ni accesible: lo recuperan aria-label y el tooltip
            // de la app (antes era un title nativo, distinto en cada sistema).
            aria-label={collapsed ? it.label : undefined}
            data-tip={collapsed ? it.label : ''}
            onClick={handleNavigate}
          >
            <span className="nav-hex">
              <Hex />
              <span className="ico">{ICONS[it.key]}</span>
            </span>
            <span className="label">{it.label}</span>
          </NavLink>
        ))}
      </nav>

      <div className="nav-foot">
        <button
          type="button"
          className="nav-toggle"
          onClick={toggle}
          aria-label={collapsed ? 'Expandir menú' : 'Colapsar menú'}
          data-tip={collapsed ? 'Expandir menú' : ''}
        >
          <span className="chev" aria-hidden="true">{collapsed ? '»' : '«'}</span>
          <span className="lbl">Colapsar</span>
        </button>
      </div>
    </aside>
  );
}
